import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import GoogleIcon from "@mui/icons-material/Google";
import FacebookIcon from "@mui/icons-material/Facebook";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneIcon from "@mui/icons-material/Phone";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import InsightsIcon from "@mui/icons-material/Insights";
import axios from "axios";
import moment from "moment";
import Links from "../../Data/Links";
import { MONTHS, YEARS, PIE_COLORS } from "../../constants";
import getFiscals from "../../utils/getFiscals";
import BarChart from "../../component/Charts/BarChart";
import { Chart as ChartJS } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(ChartDataLabels);

export default function StoreLevelMonthlyReport({
  externalStoreName,
  externalMonth,
  externalYear,
  externalDivision,
  hideFilters = false,
  hideTitle = false,
  embedded = false
} = {}) {
  const currentYear = moment().format("YYYY");
  const currentMonth = moment().format("MMM");

  const [month, setMonth] = useState(externalMonth || currentMonth);
  const [year, setYear] = useState(externalYear || currentYear);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");

  const [mtdRows, setMtdRows] = useState([]);
  const [vdpRows, setVdpRows] = useState([]);
  const [acquisitionRows, setAcquisitionRows] = useState([]);
  const [adsRows, setAdsRows] = useState([]);
  const [leadsMetrics, setLeadsMetrics] = useState({
    fup: { actual: 0, target: 0 },
    internet: { actual: 0, target: 0 },
    opportunities: { actual: 0, target: 0 }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const usedDivisions = ["DriveNation", "FFUN Cars"];
  const currentWeek = Math.ceil(moment().date() / 7) || 1;

  const STATIC_STORE_WHITELIST = [
    // Auto
    { division: "Auto", name: "Acura" },
    { division: "Auto", name: "VW" },
    { division: "Auto", name: "South 20 Dodge" },
    { division: "Auto", name: "Hyundai South" },
    { division: "Auto", name: "Hyundai North" },
    { division: "Auto", name: "Nipawin Chrysler Dodge" },
    { division: "Auto", name: "KIA" },
    { division: "Auto", name: "Country Ford" },
    { division: "Auto", name: "FFUN Dodge NB" },
    { division: "Auto", name: "Genesis" },
    { division: "Auto", name: "NAL" },
    { division: "Auto", name: "FFUN Collision" },
    // Used
    { division: "DriveNation", name: "Prince Albert" },
    { division: "DriveNation", name: "Regina" },
    { division: "DriveNation", name: "Saskatoon" },
    { division: "FFUN Cars", name: "Saskatoon" },
    // Rec
    { division: "Rec", name: "FMS-Yorkton" },
    { division: "Rec", name: "Redline HD" },
    { division: "Rec", name: "Rock HD" },
    { division: "Rec", name: "FMS-Sask" },
    { division: "Rec", name: "FFUN Motorsports Winnipeg East" },
    { division: "Rec", name: "FFUN Motorsports Portage" },
    { division: "Rec", name: "FFUN Motorsports Winnipeg West" },
    { division: "Rec", name: "FMS Nipawin" },
    { division: "Rec", name: "FFUN Carts" }
  ];

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString();
  };

  const formatSeconds = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString();
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `${Math.round(num * 100)}%`;
  };

  const calcPercent = (actual, target) => {
    if (!target || Number(target) === 0) return "0%";
    return `${Math.round((Number(actual) / Number(target)) * 100)}%`;
  };

  const parseNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const parseVehicles = (value) => {
    if (!value) return [];
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  };

  const getStoreName = (row) => {
    return (
      row?.store_name ||
      row?.store ||
      row?.dealership ||
      row?.name ||
      row?.storeName ||
      row?.store_id ||
      row?.id ||
      "-"
    );
  };


  const normalizeKeyPart = (value) => (value || "").trim().replace(/\s+/g, " ").toLowerCase();

  const allowedStores = useMemo(() => {
    const allowedKeys = new Set(
      STATIC_STORE_WHITELIST.map((item) => `${normalizeKeyPart(item.division)}|${normalizeKeyPart(item.name)}`)
    );
    return stores.filter((store) => {
      const key = `${normalizeKeyPart(store.division)}|${normalizeKeyPart(store.name)}`;
      return allowedKeys.has(key);
    });
  }, [stores]);

  const selectedStore = useMemo(
    () => allowedStores.find((store) => String(store.id) === String(storeId)),
    [allowedStores, storeId]
  );

  const driveNationStoreCount = useMemo(
    () => allowedStores.filter((store) => normalizeKeyPart(store?.division) === "drivenation").length,
    [allowedStores]
  );

  const isSharedDriveNation =
    normalizeKeyPart(selectedStore?.division) === "drivenation" && driveNationStoreCount > 1;

  const storeNameCounts = useMemo(() => {
    return allowedStores.reduce((acc, store) => {
      const key = (store?.name || "").trim().toLowerCase();
      if (key) acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [allowedStores]);

  const getStoreLabel = (store) => {
    const name = store?.name || "";
    const division = store?.division || "";
    const needsDivision = usedDivisions.includes(division) || (name && storeNameCounts[name.trim().toLowerCase()] > 1);
    return needsDivision ? `${division} - ${name}` : name;
  };

  const getMarketingDivision = (store) => {
    const division = (store?.division || "").trim();
    if (!division) return "";
    if (usedDivisions.includes(division)) return "used";
    if (division.toLowerCase() === "rec") return "rec";
    return "auto";
  };

  const getMetricValue = (row, keys, defaultValue = undefined) => {
    if (!row) return defaultValue;
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
        return row[key];
      }
    }
    return defaultValue;
  };

  const getAdsMetric = (row, keys) => {
    return parseNum(getMetricValue(row, keys, 0));
  };

  const getStoreIdentifier = (row) => {
    const id = row?.store_identifier ?? row?.store_id ?? row?.id ?? row?.storeId;
    if (id === undefined || id === null) return "";
    return `${id}`.trim();
  };

  const getMetric = (row, actualKey, targetKey) => {
    const lowerRow = Object.keys(row || {}).reduce((acc, key) => {
      acc[key.toLowerCase()] = row[key];
      return acc;
    }, {});

    const tryKeys = (obj, keys) => {
      for (const k of keys) {
        const val = obj?.[k] ?? obj?.[k.toLowerCase()];
        if (val !== undefined && val !== null && val !== "") return val;
      }
      return 0;
    };

    const actual = tryKeys(lowerRow, [
      actualKey,
      `${actualKey}_actual`, `${actualKey}actual`, `${actualKey}_act`,
      "fup_actual", "follow_up_actual", "followup_actual", "phone_up_actual", "up_actual",
      "internet_actual", "internet_leads_actual", "int_actual",
      "opportunities_actual", "opps_actual", "opps_act",
      "actual", "value", "actuals"
    ]);
    const target = tryKeys(lowerRow, [
      targetKey,
      `${targetKey}_target`, `${targetKey}target`, `${targetKey}_tgt`,
      "fup_target", "follow_up_target", "followup_target", "phone_up_target", "up_target",
      "internet_target", "internet_leads_target", "int_target",
      "opportunities_target", "opps_target", "opps_tgt",
      "target"
    ]);

    return {
      actual: parseNum(actual || 0),
      target: parseNum(target || 0)
    };
  };

  const getAverageMetric = (items, keys) => {
    if (!Array.isArray(items) || items.length === 0) return undefined;
    const values = items
      .map((item) => Number(getMetricValue(item, keys)))
      .filter((val) => Number.isFinite(val));
    if (values.length === 0) return undefined;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  };

  const getStoreMatch = (row) => {
    if (isSharedDriveNation) return true;
    const rowStoreId = row?.store_id ?? row?.storeId ?? row?.id;
    if (rowStoreId !== undefined && rowStoreId !== null && storeId !== "") {
      return String(rowStoreId) === String(storeId);
    }
    const storeName = getStoreName(row);
    return selectedStore?.name && String(storeName).trim().toLowerCase() === selectedStore.name.trim().toLowerCase();
  };

  const getAdsStoreMatch = (row) => {
    if (isSharedDriveNation) return true;
    const rowStoreId = row?.store_id ?? row?.storeId ?? row?.id;
    if (rowStoreId !== undefined && rowStoreId !== null && storeId !== "") {
      return String(rowStoreId) === String(storeId);
    }
    const rowStoreName = getStoreName(row);
    return selectedStore?.name && String(rowStoreName).trim().toLowerCase() === selectedStore.name.trim().toLowerCase();
  };

  const mtdStoreRows = mtdRows.filter(getStoreMatch);
  const mtdRow = mtdStoreRows[0] || null;
  const avgBounceRate = getAverageMetric(mtdStoreRows, ["bounce_rate", "bounceRate"]);
  const avgSessionDuration = getAverageMetric(mtdStoreRows, [
    "avg_session_duration_seconds",
    "avg_session_duration",
    "avg_session_duration_sec",
    "avg_session_duration_secs",
    "avg_session_duration_in_seconds",
    "average_session_duration",
    "average_session_duration_seconds",
    "session_duration"
  ]);

  const vdpStoreRows = vdpRows.filter(getStoreMatch);
  const aggregateVehicles = (rows, key) => {
    const vehicleMap = {};
    rows.forEach((row) => {
      const vehicles = parseVehicles(row?.[key]);
      vehicles.forEach((vehicle) => {
        const name = vehicle?.vehicle || vehicle?.name || "-";
        const events = Number(vehicle?.events || 0);
        const users = Number(vehicle?.users || 0);
        if (!vehicleMap[name]) {
          vehicleMap[name] = { vehicle: name, events: 0, users: 0 };
        }
        vehicleMap[name].events += events;
        vehicleMap[name].users += users;
      });
    });
    return Object.values(vehicleMap).sort((a, b) => b.events - a.events);
  };

  const topUsedVehicles = aggregateVehicles(vdpStoreRows, "top_used_vehicles").slice(0, 5);
  const topNewVehicles = aggregateVehicles(vdpStoreRows, "top_new_vehicles").slice(0, 5);
  const vdpTotals = vdpStoreRows.reduce(
    (acc, row) => {
      acc.srp_events += Number(row?.srp_events || 0);
      acc.srp_users += Number(row?.srp_users || 0);
      acc.used_vdp_events += Number(row?.used_vdp_events || 0);
      acc.used_vdp_users += Number(row?.used_vdp_users || 0);
      acc.new_vdp_events += Number(row?.new_vdp_events || 0);
      acc.new_vdp_users += Number(row?.new_vdp_users || 0);
      acc.other_vdp_events += Number(row?.other_vdp_events || 0);
      acc.other_vdp_users += Number(row?.other_vdp_users || 0);
      return acc;
    },
    {
      srp_events: 0,
      srp_users: 0,
      used_vdp_events: 0,
      used_vdp_users: 0,
      new_vdp_events: 0,
      new_vdp_users: 0,
      other_vdp_events: 0,
      other_vdp_users: 0
    }
  );

  const totalVdpEvents = vdpTotals.used_vdp_events + vdpTotals.new_vdp_events + vdpTotals.other_vdp_events;
  const totalVdpUsers = vdpTotals.used_vdp_users + vdpTotals.new_vdp_users + vdpTotals.other_vdp_users;

  const acquisitionStoreRows = acquisitionRows.filter(getStoreMatch);

  const getChannelName = (row) => {
    return row?.channel || row?.traffic_channel || row?.channel_name || "-";
  };

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const getGroupedAcquisitionData = () => {
    const channelMap = {};

    acquisitionStoreRows.forEach((row) => {
      const channelName = getChannelName(row);
      const sessions = toNumber(getMetricValue(row, ["sessions"]));
      const activeUsers = toNumber(getMetricValue(row, ["active_users", "activeUsers"]));
      const newUsers = toNumber(getMetricValue(row, ["new_users", "newUsers"]));

      if (!channelMap[channelName]) {
        channelMap[channelName] = { channel: channelName, sessions: 0, active_users: 0, new_users: 0 };
      }
      channelMap[channelName].sessions += sessions;
      channelMap[channelName].active_users += activeUsers;
      channelMap[channelName].new_users += newUsers;
    });

    const channelSummary = Object.values(channelMap).sort((a, b) => b.sessions - a.sessions);
    const grandTotal = channelSummary.reduce(
      (acc, item) => {
        acc.sessions += item.sessions;
        acc.active_users += item.active_users;
        acc.new_users += item.new_users;
        return acc;
      },
      { sessions: 0, active_users: 0, new_users: 0 }
    );

    return { channelSummary, grandTotal };
  };

  const { channelSummary, grandTotal } = getGroupedAcquisitionData();

  const barColors = Object.values(PIE_COLORS);
  const maxBars = 8;
  const topChannels = channelSummary.slice(0, maxBars);
  const otherChannels = channelSummary.slice(maxBars);
  const otherTotal = otherChannels.reduce((sum, item) => sum + item.sessions, 0);
  const barChannels = otherTotal > 0
    ? [...topChannels, { channel: "Other", sessions: otherTotal }]
    : topChannels;

  const sessionsBarData = {
    labels: barChannels.map((item) => item.channel),
    datasets: [
      {
        data: barChannels.map((item) => item.sessions),
        backgroundColor: barChannels.map((_, index) => barColors[index % barColors.length]),
        borderWidth: 0
      }
    ]
  };

  const sessionsBarOptions = {
    maintainAspectRatio: false,
    indexAxis: "y",
    layout: { padding: { top: 8, bottom: 8, left: 8, right: 24 } },
    scales: {
      x: {
        ticks: {
          callback: (value) => Number(value).toLocaleString()
        }
      },
      y: {
        ticks: {
          autoSkip: false
        }
      }
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#111111",
        backgroundColor: "rgba(255,255,255,0.9)",
        borderRadius: 4,
        padding: 4,
        anchor: "end",
        align: "end",
        offset: 4,
        font: { weight: "bold", size: 10 },
        formatter: (value) => {
          const total = grandTotal.sessions || 0;
          const percent = total ? Math.round((value / total) * 100) : 0;
          return `${percent}% (${value})`;
        },
        clamp: true,
        clip: false
      }
    }
  };

  const fetchAll = () => {
    if (!storeId) {
      setError("Please select a store.");
      return;
    }

    setLoading(true);
    setError("");

    const payload = {
      month: month,
      year: Number(year)
    };

    if (!isSharedDriveNation && storeId !== "" && !Number.isNaN(Number(storeId))) {
      payload.store_id = Number(storeId);
    }
    if (isSharedDriveNation && selectedStore?.name) {
      payload.store_name = selectedStore.name;
    }

    const startDate = moment(`${year}-${month}-01`, "YYYY-MMM-DD").startOf("month");
    const isCurrentMonth = year === moment().format("YYYY") && month === moment().format("MMM");
    const endDate = isCurrentMonth
      ? moment()
      : moment(`${year}-${month}-01`, "YYYY-MMM-DD").endOf("month");
    const adsPayload = {
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    };
    if (!isSharedDriveNation && payload.store_id) adsPayload.store_id = payload.store_id;
    if (selectedStore?.name) adsPayload.store_name = selectedStore.name;

    const marketingDivision = getMarketingDivision(selectedStore);
    const marketingPayload = {
      division: marketingDivision,
      month: month,
      year: year,
      week: currentWeek,
      fiscal_year: "FY" + getFiscals(month, year).toString().substring(2)
    };

    const marketingRequest = marketingDivision
      ? axios.post(Links.ceoMarketingBreakdown, marketingPayload)
      : Promise.resolve({ data: { storeData: [] } });

    const requests = [
      { key: "mtd", promise: axios.post(Links.ga4Mtd, payload) },
      { key: "vdp", promise: axios.post(Links.ga4Vdp, payload) },
      { key: "acquisition", promise: axios.post(Links.ga4Acquisition, payload) },
      { key: "marketing", promise: marketingRequest },
      { key: "googleAds", promise: axios.post(Links.googleAdsReport, adsPayload) },
      { key: "metaAds", promise: axios.post(Links.metaAdsReport, adsPayload) }
    ];

    Promise.allSettled(requests.map((item) => item.promise))
      .then((results) => {
        const resultMap = results.reduce((acc, result, index) => {
          acc[requests[index].key] = result;
          return acc;
        }, {});

        const getDataRows = (result) =>
          result?.status === "fulfilled" ? result.value?.data?.data || [] : [];

        setMtdRows(getDataRows(resultMap.mtd));
        setVdpRows(getDataRows(resultMap.vdp));
        setAcquisitionRows(getDataRows(resultMap.acquisition));

        const googleRows = getDataRows(resultMap.googleAds).map((row) => ({ ...row, __source: "Google" }));
        const metaRows = getDataRows(resultMap.metaAds).map((row) => ({ ...row, __source: "Meta" }));
        setAdsRows([...googleRows, ...metaRows]);

        const marketingRes = resultMap.marketing?.status === "fulfilled" ? resultMap.marketing.value : null;
        const storeData = marketingRes?.data?.storeData || [];
        let matchedStore = null;
        if (storeId) {
          matchedStore = storeData.find((row) => getStoreIdentifier(row) === String(storeId));
        }
        if (!matchedStore && selectedStore?.name) {
          const selectedName = selectedStore.name.trim().toLowerCase();
          const selectedDiv = (selectedStore.division || "").trim().toLowerCase();
          const matches = storeData.filter((row) => {
            const rowName = String(row?.store_name || row?.dealership || row?.name || "").trim().toLowerCase();
            return rowName === selectedName;
          });
          matchedStore =
            matches.find((row) => (row?.division || row?.Division || "").toLowerCase() === selectedDiv) ||
            matches[0] ||
            null;
        }

        const fup = getMetric(matchedStore || {}, "fup", "fup");
        const internet = getMetric(matchedStore || {}, "internet", "internet");
        const opportunities = getMetric(matchedStore || {}, "opportunities", "opportunities");

        setLeadsMetrics({ fup, internet, opportunities });

        const coreFailed = ["mtd", "vdp", "acquisition"].some(
          (key) => resultMap[key]?.status === "rejected"
        );
        if (coreFailed) {
          setError("Failed to load store level marketing data.");
        } else {
          setError("");
        }
      })
      .catch((err) => {
        console.log("Store level monthly report fetch error:", err);
        setMtdRows([]);
        setVdpRows([]);
        setAcquisitionRows([]);
        setAdsRows([]);
        setLeadsMetrics({
          fup: { actual: 0, target: 0 },
          internet: { actual: 0, target: 0 },
          opportunities: { actual: 0, target: 0 }
        });
        setError("Failed to load store level marketing data.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    axios
      .get(Links.getAllStores)
      .then((res) => {
        const storeList = res.data?.stores || [];
        setStores(storeList);
      })
      .catch((err) => {
        console.log("Stores fetch error:", err);
        setStores([]);
      });
  }, []);

  useEffect(() => {
    if (externalMonth) {
      setMonth(externalMonth);
    }
  }, [externalMonth]);

  useEffect(() => {
    if (externalYear) {
      setYear(externalYear);
    }
  }, [externalYear]);

  useEffect(() => {
    if (!externalStoreName || allowedStores.length === 0) return;
    const targetName = externalStoreName.trim().toLowerCase();
    const targetDivision = externalDivision ? externalDivision.trim().toLowerCase() : "";
    const matches = allowedStores.filter((store) =>
      (store?.name || "").trim().toLowerCase() === targetName
    );
    let nextStore = null;
    if (matches.length === 1) {
      nextStore = matches[0];
    } else if (matches.length > 1 && targetDivision) {
      nextStore =
        matches.find((store) => (store?.division || "").trim().toLowerCase() === targetDivision) ||
        matches[0];
    } else if (matches.length > 0) {
      nextStore = matches[0];
    }
    if (nextStore && String(nextStore.id) !== String(storeId)) {
      setStoreId(String(nextStore.id));
    }
  }, [externalStoreName, externalDivision, allowedStores, storeId]);

  useEffect(() => {
    if (externalStoreName) return;
    if (allowedStores.length > 0) {
      const hasSelected = allowedStores.some((store) => String(store.id) === String(storeId));
      if (!hasSelected) {
        setStoreId(String(allowedStores[0].id));
      }
    }
  }, [allowedStores, storeId, externalStoreName]);

  useEffect(() => {
    if (!storeId || hideFilters) return;
    fetchAll();
  }, [storeId, hideFilters]);

  useEffect(() => {
    if (!storeId || !hideFilters) return;
    fetchAll();
  }, [storeId, month, year, hideFilters]);

  const adsTotals = useMemo(() => {
    const filteredAdsRows = adsRows.filter(getAdsStoreMatch);
    const rowsToUse = filteredAdsRows.length > 0 ? filteredAdsRows : adsRows;
    const totalsBySource = rowsToUse.reduce(
      (acc, row) => {
        const sourceKey = row.__source || "Google";
        if (!acc[sourceKey]) {
          acc[sourceKey] = { impressions: 0, clicks: 0, spend: 0, leads: 0, conversions: 0, conversionValue: 0 };
        }
        acc[sourceKey].impressions += getAdsMetric(row, ["IMPRESSIONS", "impressions", "impression"]);
        acc[sourceKey].clicks += getAdsMetric(row, ["CLICKS", "clicks", "click"]);
        acc[sourceKey].spend += getAdsMetric(row, ["SPEND", "spend", "cost", "amount_spent"]);
        acc[sourceKey].leads += getAdsMetric(row, ["LEADS", "leads", "lead"]);
        acc[sourceKey].conversions += getAdsMetric(row, ["CONVERSIONS", "conversions", "conversion"]);
        acc[sourceKey].conversionValue += getAdsMetric(row, ["CONVERSIONS_VALUE", "conversions_value"]);
        return acc;
      },
      {}
    );

    const combined = Object.values(totalsBySource).reduce(
      (acc, item) => {
        acc.impressions += item.impressions;
        acc.clicks += item.clicks;
        acc.spend += item.spend;
        acc.leads += item.leads;
        acc.conversions += item.conversions;
        acc.conversionValue += item.conversionValue;
        return acc;
      },
      { impressions: 0, clicks: 0, spend: 0, leads: 0, conversions: 0, conversionValue: 0 }
    );

    const calcRates = (totals) => ({
      ctr: totals.impressions ? totals.clicks / totals.impressions : 0,
      cpc: totals.clicks ? totals.spend / totals.clicks : 0,
      cpl: totals.leads ? totals.spend / totals.leads : 0,
      cpa: totals.conversions ? totals.spend / totals.conversions : 0
    });

    return {
      bySource: totalsBySource,
      combined,
      combinedRates: calcRates(combined),
      googleRates: calcRates(totalsBySource.Google || { impressions: 0, clicks: 0, spend: 0, leads: 0, conversions: 0 }),
      metaRates: calcRates(totalsBySource.Meta || { impressions: 0, clicks: 0, spend: 0, leads: 0, conversions: 0 })
    };
  }, [adsRows]);

  const costPerLead = useMemo(() => {
    const internetLeads = parseNum(leadsMetrics.internet.actual);
    if (!internetLeads) return 0;
    return adsTotals.combined.spend / internetLeads;
  }, [adsTotals, leadsMetrics]);

  return (
    <div style={{ padding: embedded ? 0 : "24px" }}>
      {!hideTitle && <h2>Store Level - Monthly Report</h2>}

      {!hideFilters && (
        <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="store-level-month-label">Month</InputLabel>
              <Select
                labelId="store-level-month-label"
                value={month}
                label="Month"
                onChange={(event) => setMonth(event.target.value)}
              >
                {Object.values(MONTHS).map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="store-level-year-label">Year</InputLabel>
              <Select
                labelId="store-level-year-label"
                value={year}
                label="Year"
                onChange={(event) => setYear(event.target.value)}
              >
                {YEARS.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="store-level-store-label">Store</InputLabel>
              <Select
                labelId="store-level-store-label"
                value={storeId}
                label="Store"
                onChange={(event) => setStoreId(event.target.value)}
              >
                {allowedStores.map((store) => (
                  <MenuItem key={store.id} value={String(store.id)}>
                    {getStoreLabel(store)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchAll}
              disabled={loading}
            >
              {loading ? "Loading..." : "Load"}
            </Button>
          </Grid>
        </Grid>
      )}

      {error && (
        <div style={{ color: "#b00020", marginBottom: 16 }}>
          {error}
        </div>
      )}
      {isSharedDriveNation && (
        <div style={{ marginBottom: 16, color: "#4b5563" }}>
          Shared GA4/Ads account across DriveNation stores. Metrics shown are shared totals.
        </div>
      )}

      <h3 style={{ marginBottom: 12 }}>Leads Metrics</h3>
      <div className="table-responsive" style={{ marginBottom: 24 }}>
        <table className="table table-striped table-bordered">
          <thead>
            <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
              <th>Metric</th>
              <th>Actual</th>
              <th>Target</th>
              <th>% of Goal</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <PhoneIcon fontSize="small" sx={{ color: "#1976d2" }} />
                  F/Up &amp; P Up
                </span>
              </td>
              <td>{formatNumber(leadsMetrics.fup.actual)}</td>
              <td>{formatNumber(leadsMetrics.fup.target)}</td>
              <td>{calcPercent(leadsMetrics.fup.actual, leadsMetrics.fup.target)}</td>
            </tr>
            <tr>
              <td>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <LanguageIcon fontSize="small" sx={{ color: "#2e7d32" }} />
                  Internet Leads
                </span>
              </td>
              <td>{formatNumber(leadsMetrics.internet.actual)}</td>
              <td>{formatNumber(leadsMetrics.internet.target)}</td>
              <td>{calcPercent(leadsMetrics.internet.actual, leadsMetrics.internet.target)}</td>
            </tr>
            <tr>
              <td>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <InsightsIcon fontSize="small" sx={{ color: "#6d4c41" }} />
                  Total Leads
                </span>
              </td>
              <td>{formatNumber(leadsMetrics.opportunities.actual)}</td>
              <td>{formatNumber(leadsMetrics.opportunities.target)}</td>
              <td>{calcPercent(leadsMetrics.opportunities.actual, leadsMetrics.opportunities.target)}</td>
            </tr>
            <tr>
              <td>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <AttachMoneyIcon fontSize="small" sx={{ color: "#2e7d32" }} />
                  Cost Per Lead
                </span>
              </td>
              <td>{formatCurrency(costPerLead)}</td>
              <td>-</td>
              <td>-</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 style={{ marginBottom: 12 }}>Ads Spend</h3>
      <div className="table-responsive" style={{ marginBottom: 24 }}>
        <table className="table table-striped table-bordered">
          <thead>
            <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
              <th>Source</th>
              <th>Spend</th>
              <th>Impressions</th>
              <th>Clicks</th>
              <th>CTR</th>
              <th>CPC</th>
              <th>Leads</th>
              <th>CPL</th>
              <th>Conversions</th>
              <th>CPA</th>
              <th>Conversion Value</th>
            </tr>
          </thead>
          <tbody>
            {["Google", "Meta", "Total"].map((sourceKey) => {
              const totals = sourceKey === "Total"
                ? adsTotals.combined
                : adsTotals.bySource[sourceKey] || { impressions: 0, clicks: 0, spend: 0, leads: 0, conversions: 0, conversionValue: 0 };
              const rates = sourceKey === "Total"
                ? adsTotals.combinedRates
                : sourceKey === "Google"
                  ? adsTotals.googleRates
                  : adsTotals.metaRates;
              return (
                <tr key={sourceKey} style={sourceKey === "Total" ? { fontWeight: 600, background: "#f3f4f6" } : undefined}>
                  <td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {sourceKey === "Google" && <GoogleIcon fontSize="small" sx={{ color: "#1a73e8" }} />}
                      {sourceKey === "Meta" && <FacebookIcon fontSize="small" sx={{ color: "#1877f2" }} />}
                      {sourceKey}
                    </span>
                  </td>
                  <td>{formatCurrency(totals.spend)}</td>
                  <td>{formatNumber(totals.impressions)}</td>
                  <td>{formatNumber(totals.clicks)}</td>
                  <td>{formatPercent(rates.ctr)}</td>
                  <td>{formatCurrency(rates.cpc)}</td>
                  <td>{formatNumber(totals.leads)}</td>
                  <td>{formatCurrency(rates.cpl)}</td>
                  <td>{formatNumber(totals.conversions)}</td>
                  <td>{formatCurrency(rates.cpa)}</td>
                  <td>{formatCurrency(totals.conversionValue)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginBottom: 12 }}>Acquisition Summary</h3>
      <div style={{ marginBottom: 24, maxWidth: 676, height: 416 }}>
        <BarChart
          title=""
          data={sessionsBarData}
          options={sessionsBarOptions}
        />
      </div>

      <h3 style={{ marginBottom: 12 }}>MTD Summary</h3>
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #3f51b5", background: "#f7f8ff" }}>
            <div className="text-muted">Visitors</div>
            <div className="h5 mb-0">{formatNumber(getMetricValue(mtdRow, ["visitors"]))}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #00796b", background: "#f2fbf9" }}>
            <div className="text-muted">Sessions</div>
            <div className="h5 mb-0">{formatNumber(getMetricValue(mtdRow, ["sessions"]))}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #5d4037", background: "#fbf4f2" }}>
            <div className="text-muted">New Users</div>
            <div className="h5 mb-0">{formatNumber(getMetricValue(mtdRow, ["new_users", "newUsers"]))}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #1565c0", background: "#f2f6fb" }}>
            <div className="text-muted">VDP Views</div>
            <div className="h5 mb-0">{formatNumber(getMetricValue(mtdRow, ["vdp_views", "vdpViews"]))}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #ff8f00", background: "#fff8e1" }}>
            <div className="text-muted">Bounce Rate</div>
            <div className="h5 mb-0">{formatPercent(avgBounceRate)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #00838f", background: "#e0f7fa" }}>
            <div className="text-muted">Session Duration</div>
            <div className="h5 mb-0">{formatSeconds(avgSessionDuration)}</div>
          </div>
        </Grid>
      </Grid>

      <h3 style={{ marginBottom: 12 }}>VDP Summary</h3>
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #0d47a1", background: "#eef4ff" }}>
            <div className="text-muted">Total VDP Events</div>
            <div className="h5 mb-0">{formatNumber(totalVdpEvents)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #1b5e20", background: "#eef8f0" }}>
            <div className="text-muted">Total VDP Users</div>
            <div className="h5 mb-0">{formatNumber(totalVdpUsers)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #1565c0", background: "#f2f6fb" }}>
            <div className="text-muted">SRP Events</div>
            <div className="h5 mb-0">{formatNumber(vdpTotals.srp_events)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #5d4037", background: "#fbf4f2" }}>
            <div className="text-muted">SRP Users</div>
            <div className="h5 mb-0">{formatNumber(vdpTotals.srp_users)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #2e7d32", background: "#f1fbf4" }}>
            <div className="text-muted">Used VDP Events</div>
            <div className="h5 mb-0">{formatNumber(vdpTotals.used_vdp_events)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #00796b", background: "#f2fbf9" }}>
            <div className="text-muted">Used VDP Users</div>
            <div className="h5 mb-0">{formatNumber(vdpTotals.used_vdp_users)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #ef6c00", background: "#fff7ef" }}>
            <div className="text-muted">New VDP Events</div>
            <div className="h5 mb-0">{formatNumber(vdpTotals.new_vdp_events)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #6a1b9a", background: "#faf3ff" }}>
            <div className="text-muted">New VDP Users</div>
            <div className="h5 mb-0">{formatNumber(vdpTotals.new_vdp_users)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #455a64", background: "#f3f5f7" }}>
            <div className="text-muted">Other VDP Events</div>
            <div className="h5 mb-0">{formatNumber(vdpTotals.other_vdp_events)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #00838f", background: "#e0f7fa" }}>
            <div className="text-muted">Other VDP Users</div>
            <div className="h5 mb-0">{formatNumber(vdpTotals.other_vdp_users)}</div>
          </div>
        </Grid>
      </Grid>


      <h4 style={{ marginBottom: 8 }}>Top Vehicles</h4>
      <div className="table-responsive" style={{ marginBottom: 24 }}>
        <table className="table table-striped table-bordered">
          <thead>
            <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
              <th colSpan={3} style={{ textAlign: "center" }}>New Vehicles</th>
              <th colSpan={3} style={{ textAlign: "center" }}>Used Vehicles</th>
            </tr>
            <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
              <th>Vehicle</th>
              <th>Events</th>
              <th>Users</th>
              <th>Vehicle</th>
              <th>Events</th>
              <th>Users</th>
            </tr>
          </thead>
          <tbody>
            {(topUsedVehicles.length === 0 && topNewVehicles.length === 0 && !loading) ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  No data found.
                </td>
              </tr>
            ) : (
              Array.from({ length: Math.max(topUsedVehicles.length, topNewVehicles.length) }).map((_, index) => {
                const newVehicle = topNewVehicles[index];
                const usedVehicle = topUsedVehicles[index];
                return (
                  <tr key={`top-vehicle-row-${index}`}>
                    <td>{newVehicle?.vehicle || "-"}</td>
                    <td>{newVehicle ? formatNumber(newVehicle.events) : "-"}</td>
                    <td>{newVehicle ? formatNumber(newVehicle.users) : "-"}</td>
                    <td>{usedVehicle?.vehicle || "-"}</td>
                    <td>{usedVehicle ? formatNumber(usedVehicle.events) : "-"}</td>
                    <td>{usedVehicle ? formatNumber(usedVehicle.users) : "-"}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <h4 style={{ marginBottom: 8 }}>Channel Summary</h4>
      <div className="table-responsive" style={{ marginBottom: 24 }}>
        <table className="table table-striped table-bordered">
          <thead>
            <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
              <th>Channel</th>
              <th>Sessions</th>
              <th>Active Users</th>
              <th>New Users</th>
            </tr>
          </thead>
          <tbody>
            {channelSummary.length === 0 && !loading ? (
              <tr>
                <td colSpan={4} style={{ textAlign: "center" }}>
                  No data found.
                </td>
              </tr>
            ) : (
              channelSummary.map((item) => (
                <tr key={item.channel}>
                  <td>{item.channel}</td>
                  <td>{formatNumber(item.sessions)}</td>
                  <td>{formatNumber(item.active_users)}</td>
                  <td>{formatNumber(item.new_users)}</td>
                </tr>
              ))
            )}
          </tbody>
          {channelSummary.length > 0 && (
            <tfoot>
              <tr style={{ background: "#f3f4f6", fontWeight: 600 }}>
                <td>Total</td>
                <td>{formatNumber(grandTotal.sessions)}</td>
                <td>{formatNumber(grandTotal.active_users)}</td>
                <td>{formatNumber(grandTotal.new_users)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
